import { Formik, FormikHelpers } from 'formik';
import { observer } from 'mobx-react-lite';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { useStore } from '../../stores/store';
import FormFieldPassword from '../../common/form-field-password/form-field-password.component';
import Button from '../../common/button/button.component';
import { EditFormProps } from '../../features/services/services-subsection/services-subsection.component';
import { RevalidateOnLangChange } from '../../common/form/form.helpers';

interface IFormValues {
  password: string;
  confirmPassword: string;
  oldPassword?: string;
}

const initialValues: IFormValues = {
  password: '',
  confirmPassword: '',
  oldPassword: '',
};

const SecuritySettingsEditForm = ({ onCancel }: EditFormProps) => {
  const {
    userStore: { isLoadingUser, changePassword, gupUserData },
    commonStore: { toastSuccess },
  } = useStore();

  const { t } = useTranslation();

  const isOldPasswordRequired = useMemo(
    () => !gupUserData?.reset_content.find((x) => x === 'PASSWORD'),
    [gupUserData]
  );

  const handleFormSubmit = async (
    { password, oldPassword }: IFormValues,
    formikHelpers: FormikHelpers<IFormValues>
  ) => {
    const success = await changePassword(password, oldPassword);
    if (!success) return;

    toastSuccess(t('data_updated_successfully'));
    onCancel && onCancel();
    formikHelpers.resetForm();
  };

  const passwordRule =
    /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[@$!%*#?&])(?!.*\s).{8,30}$/;

  const validationSchema = Yup.object({
    oldPassword: Yup.string().when([], {
      is: () => isOldPasswordRequired,
      then: (s) =>
        s
          .matches(passwordRule, t('password_is_incorrect'))
          .required(t('password_is_required')),
      otherwise: (s) => s.notRequired(),
    }),

    password: Yup.string()
      .matches(passwordRule, t('password_is_incorrect'))
      .required(t('password_is_required')),

    confirmPassword: Yup.string().test(
      'passwords-match',
      t('passwords_must_match'),
      (value, ctx) => ctx.parent.password === value
    ),
  });

  return (
    <Formik
      enableReinitialize
      validationSchema={validationSchema}
      initialValues={initialValues}
      onSubmit={(values, helpers) => handleFormSubmit(values, helpers)}>
      {({ handleSubmit, isValid, isSubmitting, dirty }) => (
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}>
          <div className='inputs'>
            {isOldPasswordRequired && (
              <FormFieldPassword
                name='oldPassword'
                placeholder={t('enter_your_current_password')}
                label={t('account_settings_security_current_password')}
              />
            )}

            <FormFieldPassword
              autoComplete='new-password'
              shouldValidatePassword
              name='password'
              placeholder={t('enter_your_password')}
              label={t('new_password')}
            />

            <FormFieldPassword
              name='confirmPassword'
              placeholder={t('repeat_your_password')}
              label={t('repeat_the_password')}
            />
          </div>

          <div className='buttons'>
            <Button
              appearance='secondary'
              disabled={isSubmitting}
              onClick={() => onCancel && onCancel()}>
              {t('cancel')}
            </Button>
            <Button
              isLoading={isLoadingUser}
              disabled={!dirty || !isValid}
              type='submit'>
              {t('save_changes')}
            </Button>
          </div>
          <RevalidateOnLangChange />
        </form>
      )}
    </Formik>
  );
};

export default observer(SecuritySettingsEditForm);
